import { EllipsisOutlined, ReloadOutlined, SendOutlined } from "@ant-design/icons";

import { CollectResponse, EItemType, IItem, IItemOption } from "../../interfaces";

import CollectField from "../CollectField";
import Field from "../Field";
import Option from "./components/Option";
import InputActionButton from "../InputActionButton";
import useInput from "./useInput";
import './style.css';

const ReloadIcon = ({ disabled = false }) => (
  <ReloadOutlined
    style={{
      color: !disabled ? "#2D66A2" : "#00000040",
      scale: "-1 1",
      rotate: "45deg"
    }}
  />
)

export type InputSendResponse = {
  item: IItem;
  collectResponse?: CollectResponse[];
  optionResponse?: IItemOption[];
  singleResponse?: string | number;
}

export interface IInputProps {
  currentItem: IItem | null;
  onSend: (response: InputSendResponse) => Promise<boolean>;
  onReset: () => void;
  isLoading: boolean;
  showResetButton: boolean;
}

export default function Input({
  currentItem,
  onSend,
  isLoading,
  showResetButton,
  onReset,
}: IInputProps) {

  const {
    handleChange,
    handleSend,
    isCollect,
    isFinished,
    isInput,
    errors,
    isResponse,
    values,
    handleClickOption,
    isSubmiting,
    selectedOptions,
    showWrittingStatus,
    handleReset,
  } = useInput({ currentItem, onSend, isLoading, onReset });

  return (
    <div className={`inputContainer ${isCollect ? "inputCollectContainer" : ''}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {
          !isResponse && currentItem?.fields && currentItem.fields.map((field) => (
            <CollectField
              key={field.name}
              field={field}
              onChange={handleChange}
              value={values[field.name as keyof typeof values] || ""}
              error={errors?.[field.name]}
            />
          ))
        }

        {
          !isResponse && currentItem?.type === EItemType.OPTIONS && currentItem?.options && (
            <div className="inputOptionsContainer">
              {
                currentItem.options.map((option) => (
                  <Option
                    key={option.text}
                    option={option}
                    onClick={handleClickOption}
                    selected={!!selectedOptions?.find((selected) => option.text === selected?.text)}
                  />
                ))
              }
            </div>
          )
        }

        {
          isInput && (
            <Field
              item={currentItem!}
              onChange={handleChange}
              onBlur={(e) => handleChange(e, currentItem?.format, currentItem?.currency_min_value, currentItem?.currency_max_value, { forceValidate: true })}
              value={values[currentItem!.id as keyof typeof values] || ""}
              error={currentItem ? errors?.[currentItem.id] : undefined}
            />
          )
        }

        {
          isFinished && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontSize: "0.9rem",
                  color: "#212121",
                }}
              >
                Simulação de crédito finalizada.
              </span>

              <InputActionButton
                onClick={handleReset}
                label="Reiniciar"
                icon={<ReloadIcon />}
                style={{
                  background: !isSubmiting ? "#FFFFFF" : "#d7d7d7",
                }}
              />
            </div>
          )
        }
      </div>

      {
        !isFinished && (
          <div
            style={{
              display: "flex",
              alignItems: 'center',
              justifyContent: "space-between",
              gap: 16,
              marginTop: 24
            }}
          >
            {
              !isResponse && currentItem?.input_instructions ? (
                <span
                  style={{
                    fontSize: "0.9rem",
                    color: isCollect ? "#FFFFFF" : "#2D66A2"
                  }}
                >
                  {currentItem.input_instructions}
                </span>
              ) : (
                <div />
              )
            }

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 12,
              }}
            >
              {
                showResetButton && (
                  <InputActionButton
                    onClick={handleReset}
                    disabled={isResponse || isSubmiting || showWrittingStatus}
                    currentItemType={currentItem?.type}
                    label="Reiniciar"
                    icon={<ReloadIcon disabled={isSubmiting || showWrittingStatus} />}
                    style={{
                      background: !isSubmiting ? "#FFFFFF" : "#d7d7d7",
                    }}
                  />
                )
              }

              <InputActionButton
                onClick={handleSend}
                disabled={isResponse || isSubmiting || showWrittingStatus}
                loading={isSubmiting}
                label="Enviar"
                currentItemType={currentItem?.type}
                icon={showWrittingStatus ? (
                  <EllipsisOutlined />
                ) : (
                  <SendOutlined style={{
                    color: currentItem?.type === EItemType.COLLECT ? "#2D66A2" : "#FFFFFF",
                  }}
                  />
                )}
                style={{
                  background: !isSubmiting ? currentItem?.type === EItemType.COLLECT ? "#FFFFFF" : "#2D66A2" : "#d7d7d7",
                }}
              />
            </div>
          </div>
        )
      }
    </div>
  )
}
